"use client"

import * as React from "react"
import { Clock } from "lucide-react"
import { Breadcrumb } from "@/components/brand-kit/breadcrumb"
import { brandBreadcrumbs } from "@/lib/breadcrumbs"
import { SafeImage } from "@/components/ui/safe-image"
import { ShareBar } from "@/components/data/share-bar"
import { getTierLabel, getTierPillClasses, type BrandData } from "./types"

interface BrandHeroProps {
  brand: BrandData
  /**
   * What this brand's catalog IS — the orange H1 suffix and the fallback
   * description noun. Defaults to the appliance phrasing; a filter-family
   * brand (zero appliances, live /filter/ pages) passes its own so the page
   * stops claiming "Refrigerators & Freezers" for a brand that makes neither
   * (the glacier-fresh find, 08/23/2026).
   */
  catalogNoun?: string
}

/**
 * Brand detail page hero — centered editorial.
 *
 * Layout (top-to-bottom, centered):
 *   1. Breadcrumb: Fridge.com / Brands / {Brand}
 *   2. Brand wordmark (oversized SVG via logo_path) OR brand-name fallback
 *   3. H1 suffix in orange — "Refrigerators & Freezers" (or brand name + suffix when
 *      no logo)
 *   4. Description (from `brands.description`)
 *   5. Meta row + share — tier pill · headquarters · founding year · parent company · share
 *
 * Subtle ambient gradient (blue 5% top-center + orange 4% bottom-center) sits
 * behind the wordmark to add depth without competing with the logo's colors.
 */
export function BrandHero({ brand, catalogNoun = "Refrigerators & Freezers" }: BrandHeroProps) {
  const hasLogo = !!brand.logo_path
  const tierPalette = getTierPillClasses(brand.tier)
  const tierLabel = getTierLabel(brand.tier)
  // Canonical URL as the SSR fallback; the shared ShareBar resolves the live
  // window.location at click time, so server and client renders always agree.
  const canonicalUrl = `https://fridge.com/brands/${brand.slug}`

  return (
    <section
      className="relative isolate mx-auto max-w-[1200px] px-6 pt-10 pb-12 text-center sm:px-8 sm:pt-12 lg:px-12 lg:pt-14"
      aria-labelledby="brand-hero-h1"
    >
      {/* Ambient gradient — soft blue + orange behind the wordmark */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(620px 380px at 50% 20%, rgba(4,105,240,0.05), transparent 60%), radial-gradient(620px 320px at 50% 90%, rgba(255,153,81,0.04), transparent 60%)",
        }}
      />

      {/* Breadcrumb — uppercase, dash separators per handoff §1a. ONE trail
          (lib/breadcrumbs.ts brandBreadcrumbs): brand-page-render.tsx feeds
          the identical array to the BreadcrumbList schema (FRIDGE-269). */}
      <Breadcrumb
        trail={brandBreadcrumbs(brand)}
        className="mb-3.5 inline-block text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-500"
        listClassName="flex items-center gap-2 list-none m-0 p-0"
        itemClassName="flex items-center gap-2"
        linkClassName="transition-colors hover:text-[#ff9951]"
        currentClassName="text-gray-900"
        separatorClassName="text-gray-500"
      />

      {/* Brand wordmark — SVG with a brand-name H1 fallback when `logo_path`
          is null. Bumped 25% over the prior pass (105 lg / 82 sm / 56 mobile,
          was 84/66/45) to give the brand mark a bit more weight without
          returning to the oversized version that dominated the fold. */}
      {hasLogo ? (
        <div className="mx-auto -mt-1 mb-2 flex h-[56px] items-center justify-center sm:h-[82px] lg:h-[105px]">
          <SafeImage
            src={brand.logo_path!}
            alt={`${brand.name} logo`}
            width={450}
            height={105}
            sizes="(max-width: 560px) 60vw, (max-width: 900px) 48vw, 450px"
            className="h-full w-auto max-w-[min(450px,60vw)] object-contain"
          />
        </div>
      ) : null}

      <h1
        id="brand-hero-h1"
        className="mx-auto max-w-[880px] text-[32px] font-black leading-[0.98] tracking-[-0.04em] text-gray-900 sm:text-[46px] lg:text-[64px]"
      >
        {/* Keyword-placement standard (plan item 15): the H1 must carry the
            head term ("{Brand} refrigerators"). When the wordmark renders the
            brand name visually, the H1 still carries it as sr-only text —
            same words the logo shows, so visible/sr-only parity holds. */}
        {hasLogo ? <span className="sr-only">{brand.name} </span> : <>{brand.name} </>}
        <span className="text-[#ff9951]">{catalogNoun}</span>
      </h1>

      {/* Description — brands.description when curated; otherwise a neutral
          comparison line so the first sentence always carries the head term
          (plan item 15). States only what the page does: lists this brand's
          models with cross-retailer prices. */}
      <p className="mx-auto mt-3 mb-3.5 max-w-[680px] text-pretty text-[17px] leading-[1.55] text-gray-600">
        {brand.description ||
          `Compare ${brand.name} ${catalogNoun.toLowerCase().replace(/&amp;|&/g, 'and')} — models, specs, and prices tracked across major online appliance retailers.`}
      </p>

      {/* Meta row + share */}
      <div className="mt-4 inline-flex flex-wrap items-center justify-center gap-x-2.5 gap-y-2 text-[13px] text-gray-600">
        {tierPalette && (
          <>
            <span
              className="inline-flex items-center rounded-full px-3 py-[5px] text-[12px] font-bold tracking-[0.02em]"
              style={{ background: tierPalette.bg, color: tierPalette.fg, border: tierPalette.border }}
            >
              {tierLabel}
            </span>
            <span aria-hidden className="text-gray-300">·</span>
          </>
        )}
        {/* FRIDGE-241 — Headquarters removed from the meta row (08/07/2026).
            The curated `brands.description` above already states the location
            in prose ("…is a South Korean multinational…"), so this row repeated
            the same fact four lines below it. Follows FRIDGE-224, where the
            owner had the identical field removed from the product page's Brand
            Background panel on 08/01; that change deliberately left the brand
            and compare surfaces alone pending a decision, and this card is it.
            The `brands.headquarters` column and the "Where is X headquartered?"
            FAQ entry are untouched — the fact is still available, it simply is
            not restated as a factoid beside prose that already carries it.

            Founding year is KEPT deliberately. FRIDGE-224 removed Headquarters
            and left "Founded" standing in that same panel, and the owner's
            annotation marked only the location — "Est. 1958" and "Part of LG
            Corporation" were explicitly NOT marked. Removing it would invent a
            ruling nobody made. See the card for why this departs from its own
            done-when #3. */}
        {brand.founding_year && (
          <>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5 text-gray-400" aria-hidden />
              Est. {brand.founding_year}
            </span>
            <span aria-hidden className="text-gray-300">·</span>
          </>
        )}
        {brand.parent_company && (
          <>
            <span>Part of {brand.parent_company}</span>
            <span aria-hidden className="text-gray-300">·</span>
          </>
        )}
        {/* The one shared ShareBar (owner spec 07/20: real official full-color
            logos, no chips, one line) replaces the old grey email/copy/share
            chip cluster. */}
        <ShareBar
          variant="compact"
          title={`${brand.name} on Fridge.com`}
          pageUrl={canonicalUrl}
          label="Share"
          utmCampaign="brand"
          utmContent="brand-hero"
        />
      </div>
    </section>
  )
}
